import React from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import { BillStatement } from 'apollo/queries/autogenerate/schemas';
import { COMBINI_FEE } from 'shared/constants/fees';
import isHighPriceBill from 'shared/modules/bill/utils/is-high-price-bill';
import Icon from 'shared/modules/common/components/atoms/Icon';
import generateID from 'shared/modules/common/utils/generate-id';
import { formatToCurrency } from 'shared/utils/japanese';

const WarningMessage = (props: {
  bills: BillStatement[];
  onLinkPress?: () => void;
  id?: string;
}) => {
  const additionalProps = {
    /** Generate HTML and Test IDs  */
    ...generateID(props.id),
  };

  // You are just not going to pay, are you?
  if (props.bills.length >= 3) {
    return (
      <Text style={style.textSecondary} {...additionalProps}>
        先に発行されたご請求から順番に、お早めにお支払いください。{'\n'}
        お支払い方法については
        <TouchableOpacity
          onPress={props.onLinkPress}
          /** Generate HTML and Test IDs  */
          {...generateID(props.id && `${props.id}-link`)}
          /** Platform-specific props */
          {...Platform.select({
            web: {
              accessibilityRole: 'button',
            },
          })}
        >
          <Text style={[style.textSecondary, style.textBold, style.link]}>
            こちら
          </Text>
        </TouchableOpacity>
        からお問い合わせください。
      </Text>
    );
  }

  const hasAutoDebit = props.bills.some((bill) => bill.autoDebitSettlement);
  const isSingleBill = props.bills.length === 1;

  /* If payment cannot be confirmed, we may suspend the use of Paidy. Please pay as soon as possible */
  const singleBillNoAutoDebit = (
    <Text style={style.textSecondary} {...additionalProps}>
      お支払いが確認できない場合、ペイディのご利用を停止させていただく場合があります。お早めにお支払いください。
    </Text>
  );

  /* Please pay in order from the bill issued earlier. If payment cannot be confirmed, we may suspend the use of Paidy. Please pay as soon as possible. */
  const multipleBillsNoAutoDebit = (
    <Text style={style.textSecondary} {...additionalProps}>
      先に発行されたご請求から順番にお支払いください。お支払いが確認できない場合、ペイディのご利用を停止させていただく場合があります。お早めにお支払いください。
    </Text>
  );

  /* Since the account transfer could not be confirmed, please pay at a convenience store (up to 390 yen fee) or by bank transfer. If payment cannot be confirmed, we may suspend the use of Paidy. Please pay as soon as possible. */
  const singleBillWithAutoDebit = (
    <Text style={style.textSecondary} {...additionalProps}>
      {`口座振替が確認できなかったため、コンビニ払い（手数料最大¥${COMBINI_FEE}）もしくは銀行振込でお支払いください。\nお支払いが確認できない場合、ペイディのご利用を停止させていただく場合があります。お早めにお支払いください。`}
    </Text>
  );

  /* Since the account transfer could not be confirmed, please pay at the convenience store (up to 390 yen fee) or by bank transfer in order from the bill issued earlier. If payment cannot be confirmed, we may suspend the use of Paidy. Please pay as soon as possible. */
  const multipleBillsWithAutoDebit = (
    <Text style={style.textSecondary} {...additionalProps}>
      {`口座振替が確認できなかったため、コンビニ払い（手数料最大¥${COMBINI_FEE}）もしくは銀行振込で、先に発行されたご請求から順番にお支払いください。\nお支払いが確認できない場合、ペイディのご利用を停止させていただく場合があります。お早めにお支払いください。`}
    </Text>
  );

  const combiniIsNotAvailableOver300k = (
    <Text style={style.textSecondary} {...additionalProps}>
      ご請求金額が299,000円を超えているため、コンビニ払いはご利用いただけません。お手数ですが、銀行振込にてお支払いください。
      {'\n'}
      お支払いが確認できない場合、ペイディのご利用を停止させていただく場合があります。お早めにお支払いください。
    </Text>
  );

  const hasBillOver300k = props.bills.some((bill) => isHighPriceBill(bill));

  if (hasAutoDebit) {
    return isSingleBill ? singleBillWithAutoDebit : multipleBillsWithAutoDebit;
  } else {
    if (hasBillOver300k) {
      return combiniIsNotAvailableOver300k;
    }

    return isSingleBill ? singleBillNoAutoDebit : multipleBillsNoAutoDebit;
  }
};

type Props = {
  overdueBills: BillStatement[];
  onItemPress?: (billStatement: BillStatement) => void;
  onLinkPress?: () => void;
  style?: StyleProp<ViewStyle>;
  id?: string;
};

const OverdueNotificationCard = (props: Props) => {
  const billCount = props.overdueBills.length;

  const onPress = (bill: BillStatement) => {
    return () => props.onItemPress?.(bill);
  };

  const title = `お支払い待ちのご請求が${
    billCount > 1 ? `${billCount}か月分` : ''
  }あります。`;

  return (
    <View
      style={[style.root, props.style]}
      /** Generate HTML and Test IDs  */
      {...generateID(props.id)}
    >
      <Text style={[style.textBold, style.danger, style.marginBottom]}>
        {title}
      </Text>
      <WarningMessage
        bills={props.overdueBills}
        onLinkPress={props.onLinkPress}
        id={props.id && `${props.id}-message`}
      />
      <View style={style.separator} />
      {props.overdueBills.map((bill, index) => {
        const billingDate = new Date(bill.period.end);
        const isFirstItem =
          props.overdueBills.length === 1 ||
          (props.overdueBills.length >= 1 && !index);
        const isLastItem = index === props.overdueBills.length - 1;

        // If the bill is not yet PAID, use this. This amount changes for any user activities
        // e.g partially pay the bill or make refunds.
        // This automatically includes the lateFeeAmount if the bill becomes OVERDUE.
        // Do not use unpaidAmount + lateFeeAmount.
        const outstandingAmount = bill.outstandingAmount;

        return (
          <TouchableOpacity
            key={bill.id}
            onPress={onPress(bill)}
            disabled={!isFirstItem}
            style={[style.entry, !isLastItem && style.marginBottom]}
            /** Generate HTML and Test IDs  */
            {...generateID(props.id && `${props.id}-item-${index}`)}
            {...Platform.select({
              web: {
                accessibilityRole: 'button',
                accessibilityDisabled: !isFirstItem,
              },
            })}
          >
            <View style={style.entryIconWrapper}>
              {!isFirstItem && <View style={style.entryConnectingLine} />}
              <Icon
                name="alert"
                size="32"
                style={style.entryIcon}
                tintColor={isFirstItem ? '' : '#A1AAB0'}
              />
            </View>
            <View style={style.entryTextWrapper}>
              <Text
                style={[
                  style.textSecondary,
                  style.textBold,
                  !isFirstItem && style.disabled,
                ]}
              >
                {`${billingDate.getFullYear()}年${
                  billingDate.getMonth() + 1
                }月分のご請求`}
              </Text>
              <Text
                style={[
                  style.textSecondary,
                  style.textBold,
                  !isFirstItem && style.disabled,
                ]}
              >
                {formatToCurrency(outstandingAmount)}
              </Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    backgroundColor: 'white',
    padding: 24,
    borderRadius: 24,
  },
  textBold: {
    fontWeight: 'bold',
  },
  textSecondary: {
    fontSize: 12,
  },
  danger: {
    color: '#FF2751',
  },
  disabled: {
    color: '#A1AAB0',
  },
  link: {
    color: '#0064d2',
  },
  marginBottom: {
    marginBottom: 16,
  },
  separator: {
    marginVertical: 24,
    height: StyleSheet.hairlineWidth,
    backgroundColor: '#CFD4D8',
  },
  entry: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  entryIconWrapper: {
    position: 'relative',
  },
  entryIcon: {
    marginRight: 16,
  },
  entryConnectingLine: {
    backgroundColor: '#A1AAB0',
    width: 2,
    height: 16,
    position: 'absolute',
    top: -16,
    left: 15, // Because the icon is 32px
  },
  entryTextWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexGrow: 1,
  },
});

export default OverdueNotificationCard;
